// src/lib/ensureChain.js
//
// Guarantees the wallet is on Base Sepolia (84532) before a transaction.
// Handles the two real-world cases: wrong chain (switch) and chain not
// added to the wallet at all (EIP-3085 add, then switch is implicit).
//
// Wire it INSIDE WalletContext.sendPayment, before the transfer:
//
//   import { ensureBaseSepolia } from '../lib/ensureChain';
//   const sendPayment = async ({ to, amount, onSuccess, onError }) => {
//     try {
//       await ensureBaseSepolia();          // ← the fix for "chain id 1 vs 84532"
//       // ...existing send logic...
//     } catch (e) { onError?.(e); }
//   };
//
// Works with any injected EIP-1193 wallet (MetaMask, Rabby, Coinbase
// extension). If you route transactions through wagmi's sendTransaction,
// you can instead pass chainId: 84532 in the call AND still keep this as
// the pre-flight (some wallets reject rather than auto-switch).


import { createWalletClient, custom, parseEther } from 'viem';
import { baseSepolia } from 'viem/chains';

const BASE_SEPOLIA = {
  chainId: '0x14a34', // 84532
  chainName: 'Base Sepolia',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: ['https://sepolia.base.org'],
  blockExplorerUrls: ['https://sepolia.basescan.org'],
};

export async function ensureBaseSepolia(provider = typeof window !== 'undefined' ? window.ethereum : null) {
  if (!provider) throw new Error('No wallet found');

  const current = await provider.request({ method: 'eth_chainId' });
  if (current === BASE_SEPOLIA.chainId) return;

  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BASE_SEPOLIA.chainId }],
    });
  } catch (err) {
    // 4902: the wallet doesn't know this chain — add it (adding switches too)
    if (err?.code === 4902 || /unrecognized chain/i.test(err?.message ?? '')) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [BASE_SEPOLIA],
      });
    } else if (err?.code === 4001) {
      throw new Error('Switch to Base Sepolia to unlock — request was declined');
    } else {
      throw err;
    }
  }

  // verify — some wallets resolve the request before actually switching
  const after = await provider.request({ method: 'eth_chainId' });
  if (after !== BASE_SEPOLIA.chainId) {
    throw new Error('Wallet did not switch to Base Sepolia');
  }
}


// ---------------------------------------------------------------------
// Self-contained payment: switch chain, build a viem client WITH the
// chain object, send. Immune to the wagmi-config-missing-baseSepolia
// problem ("chain: undefined (id: 84532)"), because the chain is
// explicit at client creation AND at send.
//
//   import { sendBaseSepoliaPayment } from '../lib/ensureChain';
//   const txHash = await sendBaseSepoliaPayment({ to: creatorWallet, amountEth: priceETH });
//
// Drop into WalletContext.sendPayment as the whole implementation:
//   const sendPayment = async ({ to, amount, onSuccess, onError }) => {
//     try {
//       const hash = await sendBaseSepoliaPayment({ to, amountEth: amount });
//       await onSuccess?.(hash);
//     } catch (e) { onError?.(e); }
//   };
// ---------------------------------------------------------------------



/** Float-safe ETH string: tiny amounts stringify exponentially
 *  ("2.9e-7"), which parseEther rejects. toFixed(18) + trim avoids it. */
function ethAmountString(amountEth) {
  const n = Number(amountEth);
  if (!Number.isFinite(n) || n <= 0) throw new Error(`Invalid amount: ${amountEth}`);
  return n.toFixed(18).replace(/0+$/, '').replace(/\.$/, '');
}

export async function sendBaseSepoliaPayment({ to, amountEth, provider = typeof window !== 'undefined' ? window.ethereum : null }) {
  if (!provider) throw new Error('No wallet found');
  if (!/^0x[a-fA-F0-9]{40}$/.test(to ?? '')) throw new Error(`Invalid recipient: ${to}`);

  await ensureBaseSepolia(provider);

  const [account] = await provider.request({ method: 'eth_requestAccounts' });
  if (!account) throw new Error('No account connected');

  const client = createWalletClient({
    account,
    chain: baseSepolia,                 // ← the fix: chain is never undefined
    transport: custom(provider),
  });

  return client.sendTransaction({
    to,
    value: parseEther(ethAmountString(amountEth)),
    chain: baseSepolia,                 // ← explicit again at send
  });
}